// @ts-nocheck
// Live team feed for the team-lead dashboard: the `team-lead-real-time` SSE
// stream (roster + queue, auto-reconnecting) plus the `team-lead-stats` REST
// poll (per-agent call counters). Both pause while the tab is hidden, and a
// 1s clock drives the break/duration timers.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiCall } from '@/service/httpCall';
import { API_TEAM_LEAD_AGENT_STATS } from '@/service/url';
import { BACKEND_API_BASE_URL, CLIENT_SOURCE } from '@/utilities/constant';
import { getUserAccessToken } from '@/utilities/setting';
import { usePageVisible } from '@/hooks/usePageVisible';
import { buildRoster, rosterAlerts, rosterCounts, statsTotals } from './teamModel';

const STATS_POLL_MS = 60000;
const RECONNECT_MS = 3000;

const TEAM_LIVE_SSE_URL = `${BACKEND_API_BASE_URL.replace(/\/+$/, '')}/v2/agents/team-lead-real-time`;

export function useTeamRealtime(enabled = true) {
  const [groups, setGroups] = useState([]);
  const [queue, setQueue] = useState(null);
  const [statsRows, setStatsRows] = useState([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const pageVisible = usePageVisible();
  const streamOn = enabled && pageVisible;

  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    };
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };
    const onVisible = () => {
      if (document.hidden) stop();
      else {
        setNow(Math.floor(Date.now() / 1000));
        start();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    if (!document.hidden) start();
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      stop();
    };
  }, [enabled]);

  useEffect(() => {
    if (!streamOn) return undefined;
    const token = getUserAccessToken();
    let source = null;
    let retry = null;
    let closed = false;

    const connect = () => {
      source = new EventSource(
        `${TEAM_LIVE_SSE_URL}?token=${encodeURIComponent(token || '')}&x-client-source=${encodeURIComponent(CLIENT_SOURCE)}`,
      );
      source.onopen = () => setConnected(true);
      source.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data)?.data || {};
          setGroups(Array.isArray(payload.groups) ? payload.groups : []);
          if (payload.queue) setQueue(payload.queue);
          setConnected(true);
        } catch {
          /* ignore a malformed frame — the next one replaces it */
        }
        setLoading(false);
      };
      source.onerror = () => {
        source.close();
        setConnected(false);
        setLoading(false);
        if (!closed) {
          clearTimeout(retry);
          retry = setTimeout(connect, RECONNECT_MS);
        }
      };
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(retry);
      if (source) source.close();
      setConnected(false);
    };
  }, [streamOn]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const resp = await getApiCall(API_TEAM_LEAD_AGENT_STATS);
      setStatsRows(Array.isArray(resp) ? resp : []);
    } catch {
      /* leave the last-known rows in place */
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null;
    const poll = () => {
      if (!document.hidden) fetchStats();
      timer = setTimeout(poll, STATS_POLL_MS);
    };
    const onVisible = () => {
      if (!document.hidden) fetchStats();
    };
    document.addEventListener('visibilitychange', onVisible);
    poll();
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, fetchStats]);

  const roster = useMemo(() => buildRoster(groups, statsRows, now), [groups, statsRows, now]);
  const counts = useMemo(() => rosterCounts(roster.allAgents), [roster.allAgents]);
  const totals = useMemo(() => statsTotals(statsRows), [statsRows]);
  const alerts = useMemo(() => rosterAlerts(roster.allAgents), [roster.allAgents]);

  return {
    groups: roster.groups,
    allAgents: roster.allAgents,
    queue,
    statsRows,
    counts,
    totals,
    alerts,
    connected,
    loading,
    statsLoading,
    now,
    refreshStats: fetchStats,
  };
}

export default useTeamRealtime;
